import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { IconButton, List, ListItem, ListItemText, Stack, Tooltip, Typography } from '@mui/material';

import { TourOfferRelatedTour } from '~/types/tours/tourOffer';

import NativeTourOfferPicker from './NativeTourOfferPicker';
import NativeTourRemoveButton from './NativeTourRemoveButton';

interface Props {
  value: Array<TourOfferRelatedTour>;
  onChange: (value: Array<TourOfferRelatedTour>) => void;
  selfId: string;
  addLabel: string;
  emptyText: string;
}

/** An ordered list of tours: add by search, reorder, remove. */
export default function NativeTourRelatedList({ value, onChange, selfId, addLabel, emptyText }: Props) {
  const move = (from: number, to: number) => {
    const next = [...value];
    const [tour] = next.splice(from, 1);
    next.splice(to, 0, tour);
    onChange(next);
  };

  return (
    <Stack spacing={1}>
      {value.length === 0 ? (
        <Typography color="text.secondary">{emptyText}</Typography>
      ) : (
        <List dense disablePadding>
          {value.map((tour, index) => (
            <ListItem
              key={tour.tourOfferId}
              divider
              secondaryAction={
                <Stack direction="row">
                  <Tooltip title="Move up">
                    <span>
                      <IconButton aria-label="Move up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        aria-label="Move down"
                        disabled={index === value.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <NativeTourRemoveButton
                    label="Remove tour"
                    onClick={() => onChange(value.filter((_, i) => i !== index))}
                  />
                </Stack>
              }
            >
              <ListItemText
                primary={`${index + 1}. ${tour.name ?? 'Unnamed tour'}`}
                secondary={tour.legacyTourId ? `tour-${tour.legacyTourId}` : tour.tourOfferId}
              />
            </ListItem>
          ))}
        </List>
      )}
      <NativeTourOfferPicker
        label={addLabel}
        value={null}
        clearOnPick
        excludeIds={[selfId, ...value.map((tour) => tour.tourOfferId)]}
        onChange={(tour) => tour && onChange([...value, tour])}
      />
    </Stack>
  );
}
